// @ts-nocheck
import React, { useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableRow,
  Paper,
  TableContainer,
} from '@mui/material';

import {
  useTableDispatch,
  useTableState,
} from '../../context/TableSchemaBuilderContext';
import Cell from '../Cell/Cell';
import ColumnHeader from '../Header/ColumnHeader';
import RowHeader from '../Header/RowHeader';
import CornerCell from '../Cell/CornerCell';
import { useTableModification } from '../../hooks/useTableModification';
import { useTableKeyboard } from '../../hooks/useTableKeyboard';
import { useCellRefs } from '../../hooks/useCellRefs';
import TimeSlicedVirtualizedTableGrid from './TimeSlicedVirtualizedTableGrid';
import PaginatedTimeSlicedVirtualizedTableGrid from './PaginatedTimeSlicedVirtualizedTableGrid';
import { getColumnName } from '../../../../utils/Utils';

/**
 * Component that renders the table grid with configurable columns and rows
 * @returns {React.ReactElement} TableGrid component
 */
const TableGrid = () => {
  const { schema, config, constantId } = useTableState();
  const dispatch = useTableDispatch();
  const { handleAddRowBefore, handleAddColumnBefore } = useTableModification();
  const { setCellRef } = useCellRefs();

  const readOnly = config?.readOnly || false;
  const DEFAULT_WIDTH = 100;

  // All hooks must be called before any early returns (Rules of Hooks)
  useTableKeyboard();

  // Set the initial selection to the first cell (A1) when we first time open the builder
  useEffect(() => {
    const firstCellId = 'A1';
    dispatch({
      type: 'UPDATE_SELECTION',
      payload: {
        currentCell: firstCellId,
        cells: [firstCellId],
        anchorCell: firstCellId,
      },
    });
  }, [dispatch]);

  // Virtualization threshold - use virtualization for large grids
  const VIRTUALIZATION_THRESHOLD = 100; // 100 rows or 100 columns
  const shouldUseVirtualization =
    schema.rows > VIRTUALIZATION_THRESHOLD ||
    schema.columns > VIRTUALIZATION_THRESHOLD ||
    schema.rows * schema.columns > 1500 || // Total cells > 2500
    config?.fromConstAttributeFeature; // Always use virtualization for constant attribute feature

  /**
   * Gets column width from schema settings or uses default width
   * @param {string} columnName - Column name (e.g., 'A', 'B')
   * @returns {number} - Width in pixels
   */
  const getColumnWidth = useCallback(
    (columnName) => {
      return schema?.columnSettings?.[columnName]?.width || DEFAULT_WIDTH;
    },
    [schema?.columnSettings]
  );

  /**
   * Creates a corner cell component if not in read-only mode
   * @returns {React.ReactElement|null} Corner cell or null
   */
  const createCornerCell = useCallback(() => {
    if (!readOnly) {
      return (
        <CornerCell
          key="corner"
          onAddRowBefore={handleAddRowBefore}
          onAddColumnBefore={handleAddColumnBefore}
        />
      );
    }
    return null;
  }, [readOnly, handleAddRowBefore, handleAddColumnBefore]);

  /**
   * Creates a column header component if not in read-only mode
   * @param {number} columnIndex - Column index
   * @returns {React.ReactElement|null} Column header or null
   */
  const createColumnHeader = useCallback(
    (columnIndex) => {
      if (!readOnly) {
        const columnName = getColumnName(columnIndex);
        return (
          <ColumnHeader
            key={`col-${columnIndex}`}
            index={columnIndex}
            columnName={columnName}
          />
        );
      }
      return null;
    },
    [readOnly]
  );

  /**
   * Creates a row header component if not in read-only mode
   * @param {number} rowIndex - Row index
   * @returns {React.ReactElement|null} Row header or null
   */
  const createRowHeader = useCallback(
    (rowIndex) => {
      if (!readOnly) {
        return <RowHeader key={`row-${rowIndex}`} index={rowIndex} />;
      }
      return null;
    },
    [readOnly]
  );

  /**
   * Creates a regular cell component
   * @param {number} columnIndex - Column index
   * @param {number} rowIndex - Row index
   * @returns {React.ReactElement} Cell component
   */
  const createRegularCell = useCallback(
    (columnIndex, rowIndex) => {
      const columnName = getColumnName(columnIndex);
      const cellId = `${columnName}${rowIndex}`;
      return (
        <Cell
          key={cellId}
          cellId={cellId}
          columnName={columnName}
          rowIndex={rowIndex}
          setCellRef={setCellRef}
        />
      );
    },
    [setCellRef]
  );

  // Calculate the total width of all columns based on schema settings
  const calculateTableWidth = useCallback(() => {
    let totalWidth = 0;

    // Add width for row header column (only in edit mode)
    if (!readOnly) {
      totalWidth += 50; // Default width for row headers
    }

    // Add width for each column from the schema
    // In readOnly mode, we start from j=1 (skip the row header column)
    // In edit mode, we include all columns
    const startIndex = 1;
    for (let j = startIndex; j <= schema.columns; j += 1) {
      const columnName = getColumnName(j);
      totalWidth += getColumnWidth(columnName);
    }

    return `${totalWidth}px`;
  }, [schema.columns, getColumnWidth, readOnly]);

  const renderTable = useCallback(() => {
    const rows = [];
    // For readOnly mode, we skip the header row (i=0)
    const startRowIndex = readOnly ? 1 : 0;

    for (let i = startRowIndex; i <= schema.rows; i += 1) {
      // Create a new array for each row
      const cells = [];

      // For readOnly mode, we skip the first column (j=0)
      const startColIndex = readOnly ? 1 : 0;

      for (let j = startColIndex; j <= schema.columns; j += 1) {
        let cell = null;

        // Determine cell type and create appropriate component
        if (i === 0 && j === 0) {
          // Corner cell
          cell = createCornerCell();
        } else if (i === 0) {
          // Column header
          cell = createColumnHeader(j);
        } else if (j === 0) {
          // Row header
          cell = createRowHeader(i);
        } else {
          // Regular cell
          cell = createRegularCell(j, i);
        }

        if (cell) {
          cells.push(cell);
        }
      }

      // Only add rows that have cells
      if (cells.length > 0) {
        rows.push(<TableRow key={i}>{cells}</TableRow>);
      }
    }
    return rows;
  }, [
    schema.rows,
    schema.columns,
    createCornerCell,
    createColumnHeader,
    createRowHeader,
    createRegularCell,
    readOnly,
  ]);

  const tableWidth = calculateTableWidth();

  // Conditionally render virtualized or regular table
  if (shouldUseVirtualization) {
    // Use paginated version if constantId is available, otherwise regular virtualized
    if (constantId) {
      return <PaginatedTimeSlicedVirtualizedTableGrid />;
    }
    return <TimeSlicedVirtualizedTableGrid />;
  }

  return (
    <Paper
      elevation={1}
      sx={{
        '& .MuiPaper-root': {
          'padding-bottom': '0px',
          height: '100%',
        },
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          padding: readOnly ? 0.5 : 0,
          overflowY: 'auto',
          overflowX: 'auto',
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed', // Always use fixed layout to ensure column width consistency
            width: readOnly ? '100%' : tableWidth,
            minWidth: '100%',
            '& thead, & tbody tr:first-of-type > *': {
              position: 'sticky',
            },
            '& .MuiFormControl-root.MuiTextField-root': {
              marginTop: 0,
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
          }}
        >
          <TableBody>{renderTable()}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TableGrid;
